import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck, ChevronRight, UserPlus, UserMinus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Button, EmptyState, Pill } from "@/components/kit";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "baru saja";
  if (m < 60) return `${m}m lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j lalu`;
  return `${Math.floor(h / 24)}h lalu`;
}

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "owner" ? "/owner" : "/admin";
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("unread");

  const load = useCallback(() => api.listNotifications().then(setList).catch((e) => { toast.error(e.message); setList([]); }), []);
  useEffect(() => { load(); }, [load]);

  const readAll = async () => { try { await api.readAllNotifications(); toast.success("Semua ditandai dibaca"); load(); } catch (e) { toast.error(e.message); } };

  const openItem = async (n) => {
    if (!n.read) { await api.readNotification(n.id).catch(() => {}); load(); }
    const url = n?.data?.action_url;
    if (url) navigate(`${base}${url}`);
  };

  const items = list === null ? null : (tab === "unread" ? list.filter((n) => !n.read) : list);
  const unreadCount = list ? list.filter((n) => !n.read).length : 0;

  return (
    <div>
      <PageHeader eyebrow="Aktivitas" title="Notifikasi"
        subtitle="Event operasional: member join kelas, pembayaran tertunda, validasi membership."
        right={<Button variant="outline" onClick={readAll} data-testid="notif-read-all"><CheckCheck className="h-4 w-4" /> Tandai Dibaca</Button>} />

      <div className="mb-6 flex gap-2">
        <Pill active={tab === "unread"} onClick={() => setTab("unread")} data-testid="notif-tab-unread">Belum Dibaca{unreadCount ? ` (${unreadCount})` : ""}</Pill>
        <Pill active={tab === "all"} onClick={() => setTab("all")} data-testid="notif-tab-all">Semua</Pill>
      </div>

      {items === null ? <Loading /> : !items.length ? (
        <EmptyState icon={Bell} title={tab === "unread" ? "Tidak ada notifikasi belum dibaca" : "Belum ada notifikasi"} />
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const isCancel = n.type === "class_cancel";
            const Icon = isCancel ? UserMinus : UserPlus;
            return (
            <Card key={n.id} onClick={() => openItem(n)}
              className={`flex cursor-pointer items-start gap-3 ${n.read ? "opacity-60" : "border-[#3A3A3C]"}`}
              data-testid={`notif-${n.id}`}>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#141416]">
                <Icon className={`h-4 w-4 ${isCancel ? "text-[#FF8080]" : "text-[#8FD3E8]"}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#8FD3E8]" />}
                  <div className="text-sm font-semibold text-white">{n.title || n.message}</div>
                </div>
                {n.message && n.title && <div className="mt-1 text-[13px] text-[#8E8E93]">{n.message}</div>}
                <div className="mt-1 text-[11px] text-[#5A5A5F]">{timeAgo(n.created_at)}</div>
              </div>
              {n?.data?.action_url && <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[#5A5A5F]" />}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
